import { createContext, useContext, useMemo, useReducer } from "react";
import { worldPoints, loops, loopRect } from "../engine/geometry";
import { roundedPoints, glassOf, GLASS_TYPES } from "../engine/layout";
import { validateLayout } from "../engine/validation";
import { wrapPiece } from "../engine/wrap";
import { loadConnection } from "../lib/transport";

const MAX_HISTORY = 100;

export const initialState = {
  vehicle: null,
  stickerStudio: null,
  decalsMode: localStorage.getItem("xpattern.decalsMode") || "cut",
  pieces: [],
  past: [],
  future: [],
  selectedId: null,
  locked: false,
  tool: "select",
  rollWidth: 36,
  cutStyle: localStorage.getItem("xpattern.cutStyle") || "pull",  // Cut Board: "pull" = first piece comes off the roll toward the tinter, "push" = layout sent reversed
  rollLength: Number(localStorage.getItem("xpattern.rollLength")) || 1200,
  autoRound: localStorage.getItem("xpattern.autoRound.v16") || (localStorage.getItem("xpattern.autoRound.v15") === "small" ? "xsmall" : localStorage.getItem("xpattern.autoRound.v15")) || "xsmall",  // ⅛" on every corner (owner, 2026-10-28; ¼" before); the door templates carry a baked ¼" and a corner that already has the radius is never re-rounded
  framelessRound: localStorage.getItem("xpattern.framelessRound.v2") || "x64",  // Tesla & frameless glass: small pointy round so the film sits flush on the exposed corner  // corners come out exactly as DigiCut draws them unless the owner picks a radius
  gasket: "to",  // always starts on "Cut to gasket" (owner's call) — Below gasket is a per-session choice
  topExtend: Number(localStorage.getItem("xpattern.topExtend.v2") ?? 0) || 0, // Top +0 by default on every roll-up
  edgeOffset: Number(localStorage.getItem("xpattern.edgeOffset") ?? 0) || 0, // ±1/32″ all-around nudge for flush cuts
  shadowGap: Number(localStorage.getItem("xpattern.shadowGap.v1")) || 0.125, // Shadow Cut: weeding contour this far outside every picked pattern
  shadowMode: false, // Shadow Cut box ticked: contours drawn + cut for the picked patterns; a click on a pattern picks / unpicks it
  eyebrow: null, // { depth, mode } while the eyebrow cut line is being placed on the selected windshield
  knife: null, // { a, b } world points while the trim-knife line is drawn across the selected decal
  wrap: null, // { depth } while the PPF wrap tool is open on the selected pattern (W)
  wrapPresets: {}, // pattern id → { segs, depth } saved for this shop; applied when the panel lands on the roll
  liveServices: {},
  hubShown: {},
  newVehicles: 0,
  spacing: 0.25,
  margin: 0.5,
  rotationSet: "fine",
  snap: true,
  gridSize: 0.5,
  showGrid: false,
  showCutBoard: localStorage.getItem("xpattern.cutBoard") !== "0", // the floating cut-board read-out above the status bar (Tools menu / × on the bar)
  showDims: false,
  offsetPreview: 0,
  machines: [],
  machine: null,
  machineSource: null,
  machinePinned: false,
  weedBox: false,
  machineSettings: { speed: 30, travel_speed: 60, force: 80, blade_offset_mm: 0.25, passes: 1, return_home: true, feed_out: true, offset_compensation: false, curve_slowdown: false },
  savedCutSettings: null, // account-wide speed/force/… loaded from the server; survives reloads and plotter auto-detect
  savedMachine: null, // { machine_id, pinned } from the server
  cutSettingsLoaded: false,
  cutSettingsTouched: false,
  connection: loadConnection(),
  calibration: { scale_width: 1, scale_length: 1 }, // account-wide, loaded from the server on start (never from this browser)
  nestResult: null,
  fitNonce: 0,
  zoomRequest: null,
  dialog: null,
  importPrefill: null,
  loadingPatterns: false,
};

// A panel that just landed on the roll picks up the shop's saved wrap for it (edges + depth) — see WrapBar "Remember for this panel".
function applyWrapPresets(state, pieces) {
  const presets = state.wrapPresets || {};
  const was = new Set(state.pieces.filter((p) => p.included).map((p) => p.id));
  return pieces.map((p) => {
    const preset = presets[p.id];
    if (!preset || !p.included || was.has(p.id) || p.wrap) return p;
    return wrapPiece(p, preset.segs, preset.depth);
  });
}

function reducer(state, action) {
  switch (action.type) {
    case "SET":
      return { ...state, ...action.patch, ...("machineSettings" in action.patch ? { cutSettingsTouched: true } : {}) };
    case "SELECT_MACHINE": {
      // Start-up / auto-detect: the model may change but the customer's saved speed, force, passes … must not.
      // Machine-specific keys (relative moves, blade offset) only carry over when they were saved for this same model.
      const m = action.machine;
      let ms = { speed: m.default_speed, travel_speed: 60, force: m.default_force, blade_offset_mm: m.blade_offset_mm, passes: 1, return_home: true, feed_out: true, offset_compensation: false, curve_slowdown: false, relative: Boolean(m.relative) };
      if (state.savedCutSettings) {
        const { relative, blade_offset_mm, ...portable } = state.savedCutSettings;
        ms = { ...ms, ...portable };
        if (state.savedMachine?.machine_id === m.id) {
          if (relative !== undefined) ms.relative = relative;
          if (blade_offset_mm !== undefined) ms.blade_offset_mm = blade_offset_mm;
        }
      }
      return { ...state, machine: m, machineSettings: ms, ...(action.extra || {}) };
    }
    case "LIBRARY_CHANGED":
      return { ...state, libraryNonce: (state.libraryNonce || 0) + 1 };
    case "SET_AUTO_ROUND": {
      // Corner radius for every door / quarter window as it loads ("off" = exactly as DigiCut draws). Untouched patterns on the roll re-round now.
      localStorage.setItem("xpattern.autoRound.v16", action.value);
      const pieces = state.pieces.map((p) => {
        if (p.edited || p.appliedOffset || !p.rawPoints || p.decalId) return p;
        const pts = roundedPoints(p, action.value, state.gasket, state.framelessRound);
        return { ...p, points: pts, basePoints: pts, dirty: false };
      });
      return { ...state, autoRound: action.value, pieces };
    }
    case "REFRESH_OUTLINES": {
      // The server served outlines from the previous clean-up's cache and has now re-cleaned them: swap the fresh outline into
      // every untouched piece in place (position, rotation and roll stay). Pieces the tinter edited keep their work.
      const fresh = new Map(action.patterns.filter((x) => !x.clean_pending).map((x) => [x.id, x]));
      let changed = 0;
      const pieces = state.pieces.map((p) => {
        const np = fresh.get(p.patternId);
        if (!np || p.edited || p.appliedOffset || p.decalId || !np.points || JSON.stringify(np.points) === JSON.stringify(p.rawPoints)) return p;
        changed++;
        const pts = roundedPoints({ ...p, rawPoints: np.points, holes: np.holes || [] }, state.autoRound, state.gasket, state.framelessRound);
        return { ...p, rawPoints: np.points, points: pts, basePoints: pts, holes: np.holes || [], dirty: false };
      });
      return changed ? { ...state, pieces, outlinesRefreshed: (state.outlinesRefreshed || 0) + changed } : state;
    }
    case "SET_VEHICLE_GLASS": {
      const vehicle = state.vehicle ? { ...state.vehicle, ...action.glass } : state.vehicle;
      const glass = glassOf(vehicle);
      const pieces = state.pieces.map((p) => {
        if (!p.frameless || !GLASS_TYPES.includes(p.pieceType) || p.edited || p.appliedOffset || !p.rawPoints || p.decalId) return p;
        const pts = roundedPoints({ ...p, glass }, state.autoRound, state.gasket, state.framelessRound);
        return { ...p, glass, points: pts, basePoints: pts, dirty: false };
      });
      return { ...state, vehicle, pieces };
    }
    case "SET_FRAMELESS_ROUND": {
      localStorage.setItem("xpattern.framelessRound.v2", action.value);
      const pieces = state.pieces.map((p) => {
        if (!p.frameless || p.edited || p.appliedOffset || !p.rawPoints || p.decalId) return p;
        const pts = roundedPoints(p, state.autoRound, state.gasket, action.value);
        return { ...p, points: pts, basePoints: pts, dirty: false };
      });
      return { ...state, framelessRound: action.value, pieces };
    }
    case "REMOVE_PIECE":
      return { ...state, pieces: state.pieces.filter((p) => p.id !== action.id), selectedId: state.selectedId === action.id ? null : state.selectedId };
    case "LOAD_PIECES":
      return { ...state, pieces: action.pieces, past: [], future: [], selectedId: null, nestResult: action.nestResult || null, fitNonce: state.fitNonce + 1 };
    case "BEGIN_TX":
      return { ...state, past: [...state.past.slice(-MAX_HISTORY), state.pieces], future: [] };
    case "SET_PIECES": {
      const past = action.commit ? [...state.past.slice(-MAX_HISTORY), state.pieces] : state.past;
      return { ...state, pieces: applyWrapPresets(state, action.pieces), past, future: action.commit ? [] : state.future, nestResult: action.nestResult ?? state.nestResult };
    }
    case "UPDATE_PIECE": {
      const pieces = applyWrapPresets(state, state.pieces.map((p) => (p.id === action.id ? { ...p, ...action.patch } : p)));
      const past = action.commit ? [...state.past.slice(-MAX_HISTORY), state.pieces] : state.past;
      return { ...state, pieces, past, future: action.commit ? [] : state.future };
    }
    case "UNDO": {
      if (!state.past.length) return state;
      const prev = state.past[state.past.length - 1];
      return { ...state, pieces: prev, past: state.past.slice(0, -1), future: [state.pieces, ...state.future] };
    }
    case "REDO": {
      if (!state.future.length) return state;
      const [next, ...rest] = state.future;
      return { ...state, pieces: next, past: [...state.past, state.pieces], future: rest };
    }
    default:
      return state;
  }
}

const Ctx = createContext(null);

export function WorkspaceProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const worlds = useMemo(() => Object.fromEntries(state.pieces.map((p) => [p.id, p.multi ? worldPoints({ ...p, points: loopRect(loops(p)) }) : worldPoints(p)])), [state.pieces]);  // multi-part decals: the frame around every letter / mark
  const issues = useMemo(
    () => validateLayout({ pieces: state.pieces, rollWidth: state.rollWidth, margin: state.margin, spacing: state.spacing, machine: state.machine }),
    [state.pieces, state.rollWidth, state.margin, state.spacing, state.machine]
  );
  const value = useMemo(() => ({ state, dispatch, worlds, issues }), [state, worlds, issues]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useWorkspace = () => useContext(Ctx);
